import React from 'react'
import { Box, Typography, Skeleton } from '@mui/material'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'

/**
 * SupportQRCode - Reusable component for displaying support QR codes
 * 
 * This component is used across the app to show WhatsApp support QR codes.
 * It supports both media ID (preferred) and direct URL fallback.
 * 
 * @param {string} qrCodeMediaId - Media ID from clinic brand (preferred)
 * @param {string} qrCodeUrl - Direct URL to QR code image (fallback)
 * @param {string} title - Title text (default: "Need Help?")
 * @param {string} subtitle - Subtitle text (default: "Scan to reach support")
 * @param {string} size - QR code size: 'small' (100px), 'medium' (140px), 'large' (180px)
 * @param {boolean} showTitle - Whether to show title/subtitle (default: true)
 * @param {object} sx - Additional MUI sx styling
 */
export default function SupportQRCode({
  qrCodeMediaId,
  qrCodeUrl,
  title = 'Need Help?',
  subtitle = 'Scan the QR code below to reach out to our support team',
  size = 'medium',
  showTitle = true,
  sx = {},
}) {
  const [imageLoaded, setImageLoaded] = React.useState(false)
  const [imageError, setImageError] = React.useState(false)

  // Size mapping
  const sizeMap = {
    small: { qr: 100, container: 120 },
    medium: { qr: 140, container: 170 },
    large: { qr: 180, container: 210 },
  }

  const dimensions = sizeMap[size] || sizeMap.medium

  // Determine the image source
  // getImageLink expects a string (media ID), not an object
  const imageSrc = qrCodeMediaId 
    ? getImageLink(qrCodeMediaId)
    : qrCodeUrl

  const handleImageLoad = () => {
    setImageLoaded(true)
    setImageError(false)
  }

  const handleImageError = () => {
    setImageError(true)
    setImageLoaded(false)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        ...sx,
      }}
    >
      {/* Title Section */}
      {showTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupportAgentIcon 
            sx={{ 
              color: '#8b5cf6', 
              fontSize: { xs: 20, md: 24 } 
            }} 
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: { xs: '16px', md: '18px' },
            }}
          >
            {title}
          </Typography>
        </Box>
      )}

      {/* Subtitle */}
      {showTitle && subtitle && (
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: { xs: '13px', md: '14px' },
            textAlign: 'center',
            maxWidth: '280px',
          }}
        >
          {subtitle}
        </Typography>
      )}

      {/* QR Code Container */}
      <Box
        sx={{
          width: dimensions.container,
          height: dimensions.container,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '16px',
          padding: '12px',
          boxShadow: '0 4px 16px rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.15)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Loading skeleton */}
        {!imageLoaded && !imageError && imageSrc && (
          <Skeleton
            variant="rectangular"
            width={dimensions.qr}
            height={dimensions.qr}
            sx={{ 
              borderRadius: '8px',
              position: 'absolute',
            }}
          />
        )}

        {/* QR Code Image */}
        {imageSrc && !imageError ? (
          <img
            src={imageSrc}
            alt="Support QR Code"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              width: dimensions.qr,
              height: dimensions.qr,
              borderRadius: '8px',
              objectFit: 'contain',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          />
        ) : (
          /* Placeholder when no QR code available */
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: dimensions.qr,
              height: dimensions.qr,
              background: 'rgba(139, 92, 246, 0.05)',
              borderRadius: '8px',
              border: '2px dashed rgba(139, 92, 246, 0.2)',
            }}
          >
            <QrCode2Icon
              sx={{
                fontSize: dimensions.qr * 0.4,
                color: 'rgba(139, 92, 246, 0.4)',
                mb: 1,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(0, 0, 0, 0.4)',
                fontSize: '11px',
                textAlign: 'center',
              }}
            >
              Support QR
            </Typography>
          </Box>
        )}
      </Box>

      {/* Bottom label */}
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(0, 0, 0, 0.5)',
          fontSize: '11px',
          fontStyle: 'italic',
        }}
      >
        Scan with your phone camera
      </Typography>
    </Box>
  )
}

/**
 * Compact version of SupportQRCode for inline use
 */
export function SupportQRCodeCompact({ qrCodeMediaId, qrCodeUrl, onClick }) {
  // getImageLink expects a string (media ID), not an object
  const imageSrc = qrCodeMediaId 
    ? getImageLink(qrCodeMediaId)
    : qrCodeUrl

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        padding: '10px 14px',
        background: 'rgba(139, 92, 246, 0.08)',
        borderRadius: '12px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': onClick ? {
          background: 'rgba(139, 92, 246, 0.12)',
          transform: 'translateY(-1px)',
        } : {},
      }}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt="Support QR"
          style={{
            width: 40,
            height: 40,
            borderRadius: '6px',
            objectFit: 'contain',
          }}
        />
      ) : (
        <QrCode2Icon sx={{ fontSize: 28, color: '#8b5cf6' }} />
      )}
      <Box>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#8b5cf6',
          }}
        >
          Need Help?
        </Typography>
        <Typography
          sx={{
            fontSize: '11px',
            color: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          Scan for support
        </Typography>
      </Box>
    </Box>
  )
}

